export const STATE_APP_TEMPLATE = `
  <header class="site-header">
    <a class="brand" href="./" aria-label="Collaborative State Demos home">
      <span class="brand__mark" aria-hidden="true">CS</span>
      <span>
        <strong>Collaborative State Demos</strong>
        <small>Public alpha · Milestone 2</small>
      </span>
    </a>
    <nav class="site-nav module-nav" aria-label="Demo modules">
      <a href="./">JSON Merge</a>
      <a href="?demo=semantic-ast" aria-current="page">Validators + AST</a>
      <a href="?demo=tct-delivery">TCT Delivery</a>
      <a href="?demo=guided-story">Guided Story</a>
      <a href="#state-evidence">Evidence</a>
    </nav>
  </header>

  <main id="top">
    <section class="hero state-hero" aria-labelledby="state-hero-title">
      <div class="hero__copy">
        <p class="eyebrow">Local HTTP state simulation · Drafts -01 and -02</p>
        <h1 id="state-hero-title">One state.<br /><em>Two identities.</em><br />One safe commit.</h1>
        <p class="hero__lede">
          See where ordinary ETags stop, where Semantic-ETag applies, and how
          AST makes a shared-state transition conditional and atomic.
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href="#state-workbench">Open the timeline</a>
          <a class="button button--quiet" href="./">Return to JSON merge</a>
        </div>
      </div>
      <aside class="state-hero__map" aria-label="Representation and semantic validator map">
        <article class="resource-card resource-card--projection">
          <span>Projected resource</span>
          <strong>/article/123</strong>
          <code>ETag: &quot;html-v52&quot;</code>
        </article>
        <div class="semantic-rail" aria-hidden="true">
          <span>same logical state</span>
          <strong>Semantic-ETag: &quot;article-state-v7&quot;</strong>
        </div>
        <article class="resource-card resource-card--sbr">
          <span>State-Bearing Resource</span>
          <strong>/api/article/123</strong>
          <code>ETag: &quot;json-v18&quot;</code>
        </article>
      </aside>
    </section>

    <section class="boundary-note" aria-label="Simulation boundary">
      <strong>Deterministic local simulation</strong>
      <span>No HTTP request is sent. This is not an origin implementation, validator, conformance certificate, or IETF endorsement.</span>
    </section>

    <section class="state-workbench section" id="state-workbench" aria-labelledby="state-workbench-title">
      <div class="section-heading">
        <div>
          <p class="eyebrow">State transition workbench</p>
          <h2 id="state-workbench-title">Select an exchange. Follow every boundary.</h2>
        </div>
        <label class="scenario-picker">
          <span>Demonstrated scenario</span>
          <select id="state-scenario-select" aria-describedby="state-scenario-count"></select>
          <small id="state-scenario-count"></small>
        </label>
      </div>

      <article class="scenario-explanation state-scenario-explanation" aria-live="polite">
        <div>
          <p class="eyebrow" id="state-scenario-category"></p>
          <h3 id="state-scenario-title"></h3>
          <p id="state-scenario-summary"></p>
        </div>
        <div class="scenario-rule">
          <strong>Rule</strong>
          <span id="state-scenario-rule"></span>
          <div class="draft-reference-list" id="state-draft-links"></div>
        </div>
      </article>

      <section class="state-ledger" aria-label="State before and after">
        <article>
          <span class="state-ledger__label">Before client exchange</span>
          <strong id="state-before-revision">Revision 7</strong>
          <dl>
            <div><dt>SBR ETag</dt><dd id="state-before-sbr"></dd></div>
            <div><dt>Projection ETag</dt><dd id="state-before-projection"></dd></div>
            <div><dt>Semantic-ETag</dt><dd id="state-before-semantic"></dd></div>
          </dl>
        </article>
        <div class="state-ledger__transition" aria-hidden="true">→</div>
        <article>
          <span class="state-ledger__label">After complete exchange</span>
          <strong id="state-after-revision">Revision 7</strong>
          <dl>
            <div><dt>SBR ETag</dt><dd id="state-after-sbr"></dd></div>
            <div><dt>Projection ETag</dt><dd id="state-after-projection"></dd></div>
            <div><dt>Semantic-ETag</dt><dd id="state-after-semantic"></dd></div>
          </dl>
        </article>
        <aside class="state-outcome" id="state-outcome" aria-live="polite">
          <span id="state-mode-label"></span>
          <strong id="state-outcome-label"></strong>
          <p id="state-outcome-explanation"></p>
          <small id="state-outcome-counts"></small>
        </aside>
      </section>

      <section class="timeline-section" aria-labelledby="timeline-title">
        <div class="timeline-section__heading">
          <p class="eyebrow">Complete exchange</p>
          <h3 id="timeline-title">Observed state → condition → comparison → result</h3>
        </div>
        <div class="state-timeline" id="state-timeline"></div>
      </section>
    </section>

    <section class="concepts state-principles section" id="state-principles" aria-labelledby="state-principles-title">
      <div class="section-heading section-heading--compact">
        <div>
          <p class="eyebrow">Four rules to carry forward</p>
          <h2 id="state-principles-title">Coordination without validator confusion.</h2>
        </div>
      </div>
      <div class="concept-grid">
        <article><span>01</span><h3>ETag stays scoped</h3><p>An ordinary ETag validates the selected representation at its request target. It cannot be moved to another resource.</p></article>
        <article><span>02</span><h3>Semantic is not cache</h3><p>Semantic-ETag identifies logical state. It does not replace ETag, define a cache key, or independently authorize 304.</p></article>
        <article><span>03</span><h3>Capability is positive</h3><p>Seeing Semantic-ETag is not enough. Mutation safety requires explicit resource-and-method-specific enforcement knowledge.</p></article>
        <article><span>04</span><h3>412 means reconcile</h3><p>A current validator returned after failure is advisory. Retrieve or validate current state before constructing another write.</p></article>
      </div>
    </section>

    <section class="evidence section" id="state-evidence" aria-labelledby="state-evidence-title">
      <div class="evidence__copy">
        <p class="eyebrow">Published foundations</p>
        <h2 id="state-evidence-title">Two drafts, one explanatory timeline.</h2>
        <p>
          The simulator is specification-aligned educational code. It does not
          contact an origin or independently prove server conformance.
        </p>
      </div>
      <dl class="evidence-list">
        <div><dt>Semantic fields</dt><dd>draft-jurkovikj-http-semantic-validator-01</dd></div>
        <div><dt>HTTP profile</dt><dd>draft-jurkovikj-httpapi-agentic-state-02</dd></div>
        <div><dt>Scenarios</dt><dd id="state-evidence-count">8 deterministic local cases</dd></div>
        <div><dt>Network behavior</dt><dd>packaged static assets only · no simulated request leaves the browser</dd></div>
      </dl>
    </section>
  </main>

  <footer class="site-footer">
    <p>Collaborative State Demos · non-normative, browser-only explanation</p>
    <div>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-http-semantic-validator/" target="_blank" rel="noreferrer">Semantic Validators</a>
      <a href="https://datatracker.ietf.org/doc/draft-jurkovikj-httpapi-agentic-state/" target="_blank" rel="noreferrer">AST</a>
      <a href="https://github.com/antunjurkovic-collab/collaborative-state-demos" target="_blank" rel="noreferrer">Source</a>
    </div>
  </footer>
`;
